import { Backdrop, CircularProgress, Dialog } from "@mui/material";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Edit,
  X
} from "feather-icons-react/build/IconComponents";
import { t } from "i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CalendarFill } from "react-bootstrap-icons";
import DatePicker from "react-datepicker";
import API from "../../API/newIndex";
import { NOPHOTO_IMAGE } from "../../Assets/StaticData/StaticImage";
import { GenerateSlugName } from "../../controllers/GenerateSlugName";
import { clientData } from "../../controllers/GetClient";
import ImageUploader from "../../controllers/ImageUploader";
import { Transition } from "../../controllers/Transition";
import UnsavedChangesModal from "../../controllers/UnsavedChangesModal";
import { useRoutingByType } from "../../controllers/useRouting";
import useUnsavedChanges from "../../controllers/useUnsavedChanges";
import { uploadTextEditorImage } from "../../utilities/staticFunction";
import AiTextGenerate from "../AiTextGenerate";
import { ButtonComponent, DetailsComponent, InputComponent, SelectComponent, RichTextEditor } from '@shopeasy-dashboard-ui/ui-library';
import Notification from "../Notification";

const getChangedFields = (currentValues, originalValues) => {
  if (!currentValues || !originalValues) return currentValues || {};
  const changed = {};
  Object.keys(currentValues).forEach((key) => {
    const cur = currentValues[key];
    const orig = originalValues[key];
    if (JSON.stringify(cur) !== JSON.stringify(orig)) {
      changed[key] = cur;
    }
  });
  return changed;
};

const BulkEditPortfolio = ({
  isBulkEdit = true,
  bulkPortfolioIds = [],
  onClose,
  onSuccess,
}) => {
  const [bulkPortfoliosList, setBulkPortfoliosList] = useState([]);
  const [activeBulkIndex, setActiveBulkIndex] = useState(0);
  const [portfolioSearchTerm, setPortfolioSearchTerm] = useState("");
  const bulkPortfoliosDataRef = useRef({});

  const [isNotification, setIsNotification] = useState(false);
  const [notificationMsg, setNotificationMsg] = useState("");
  const [severity, setSeverity] = useState();
  const [btnLoading, setBtnLoading] = useState(false);
  const [formSpinner, setFormSpinner] = useState(false);
  const [AiLoader, setAiloader] = useState(false);

  // Form State for Active Portfolio Item (Exact fields matching AddPortfolio.js)
  const [warn, setWarn] = useState(false);
  const [enableSlug, setEnableSlug] = useState(false);
  const slugInputRef = useRef(null);

  const [title, setTitle] = useState("");
  const [slugName, setSlugName] = useState("");
  const [selectCategory, setSelectCategory] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState("");
  const [meta, setMeta] = useState({ meta_title: "", meta_description: "" });
  const { meta_title, meta_description } = meta;
  const [isMetaTitleCustom, setIsMetaTitleCustom] = useState(false);
  const [slugEdited, setSlugEdited] = useState(false);
  const [isSchedule, setIsSchedule] = useState(false);
  const [scheduleTimeDate, setScheduleTimeDate] = useState(null);

  const originalDataRef = useRef(null);
  const originalPayloadRef = useRef(null);

  const [Category, setCategory] = useState([]);
  const portfolioRoute = useRoutingByType("portfolio");
  const protocol = process.env.REACT_APP_ENVIRONMENT === "DEV" ? "http://" : "https://";
  const clientInfo = clientData;

  const buildPayload = (formData = {}) => {
    return {
      title: formData.title || "",
      portfolio_slug_name: formData.slugName || "",
      portfoliocategory: formData.selectCategory || null,
      description: formData.description || "",
      image: formData.image || "",
      meta_title: formData.meta_title || "",
      meta_description: formData.meta_description || "",
      portfolio_publish_date: formData.scheduleTimeDate || undefined,
    };
  };

  const currentFormValues = useMemo(() => {
    return {
      title,
      slugName,
      selectCategory,
      description,
      image,
      meta_title,
      meta_description,
      scheduleTimeDate,
    };
  }, [
    title,
    slugName,
    selectCategory,
    description,
    image,
    meta_title,
    meta_description,
    scheduleTimeDate,
  ]);

  const GetCategory = async () => {
    try {
      const data = await API({
        url: `/admin/portfoliocategory/GetPortfolioCategoriesWithParent?default=true`,
        method: "get",
        params: { limit: "all" },
      });
      if (data.status === 200 || data.status === 304) {
        const result = data?.data;
        setCategory(result?.data?.records || []);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  useEffect(() => {
    GetCategory();
  }, []);

  const handleSlug = async (slug) => {
    if (slug?.length) {
      const generated = await GenerateSlugName(slug);
      setSlugName(generated);
    } else {
      setSlugName("");
    }
  };

  const applyPortfolioToForm = (rawPortfolio) => {
    if (!rawPortfolio) return;
    const catId = rawPortfolio?.portfoliocategory
      ? (typeof rawPortfolio.portfoliocategory === "object"
        ? rawPortfolio.portfoliocategory._id || rawPortfolio.portfoliocategory.id
        : String(rawPortfolio.portfoliocategory))
      : "";

    const initialPublishDate = rawPortfolio?.portfolio_publish_date ? new Date(rawPortfolio.portfolio_publish_date) : null;

    const initialFormValues = {
      title: rawPortfolio?.title || "",
      slugName: rawPortfolio?.portfolio_slug_name || rawPortfolio?.slug || "",
      selectCategory: catId,
      description: rawPortfolio?.description || "",
      image: rawPortfolio?.image || "",
      meta_title: rawPortfolio?.meta_title || "",
      meta_description: rawPortfolio?.meta_description || "",
      scheduleTimeDate: initialPublishDate,
    };

    originalDataRef.current = initialFormValues;
    originalPayloadRef.current = buildPayload(initialFormValues);

    setTitle(initialFormValues.title);
    setSlugName(initialFormValues.slugName);
    setSlugEdited(Boolean(initialFormValues.slugName));
    setSelectCategory(initialFormValues.selectCategory);
    setDescription(initialFormValues.description);
    setImage(initialFormValues.image);
    setMeta({
      meta_title: initialFormValues.meta_title,
      meta_description: initialFormValues.meta_description,
    });
    setIsMetaTitleCustom(Boolean(rawPortfolio?.meta_title));
    setScheduleTimeDate(initialPublishDate);
    setWarn(false);
    setEnableSlug(false);
  };

  const fetchPortfoliosByIds = async () => {
    if (!bulkPortfolioIds || bulkPortfolioIds.length === 0) return;
    setFormSpinner(true);
    try {
      const res = await API({
        url: "/admin/portfolio/GetPortfolioDataByIds",
        method: "post",
        data: { portfolios: bulkPortfolioIds, ids: bulkPortfolioIds, portfolio_ids: bulkPortfolioIds, id: bulkPortfolioIds },
      });

      let loadedRecords = [];
      if (res?.status === 200 || res?.status === 304) {
        const resultData = res?.data;
        loadedRecords =
          resultData?.data?.records ||
          resultData?.records ||
          (Array.isArray(resultData?.data) ? resultData.data : []) ||
          (Array.isArray(resultData) ? resultData : []);
      }

      if (loadedRecords.length === 0) {
        const individualPromises = bulkPortfolioIds.map((id) =>
          API({
            url: `/admin/portfolio/getparticularPortfolio/${id}`,
            method: "get",
          })
            .then((r) => r?.data?.data)
            .catch(() => null)
        );
        const results = await Promise.all(individualPromises);
        loadedRecords = results.filter(Boolean);
      }

      setBulkPortfoliosList(loadedRecords);

      const initialCache = {};
      loadedRecords.forEach((item) => {
        const initialPublishDate = item?.portfolio_publish_date ? new Date(item.portfolio_publish_date) : null;
        initialCache[item._id] = {
          raw: item,
          values: {
            title: item?.title || "",
            slugName: item?.portfolio_slug_name || item?.slug || "",
            selectCategory: item?.portfoliocategory
              ? (typeof item.portfoliocategory === "object"
                ? item.portfoliocategory._id || item.portfoliocategory.id
                : String(item.portfoliocategory))
              : "",
            description: item?.description || "",
            image: item?.image || "",
            meta_title: item?.meta_title || "",
            meta_description: item?.meta_description || "",
            scheduleTimeDate: initialPublishDate,
          },
          original: {
            title: item?.title || "",
            slugName: item?.portfolio_slug_name || item?.slug || "",
            selectCategory: item?.portfoliocategory
              ? (typeof item.portfoliocategory === "object"
                ? item.portfoliocategory._id || item.portfoliocategory.id
                : String(item.portfoliocategory))
              : "",
            description: item?.description || "",
            image: item?.image || "",
            meta_title: item?.meta_title || "",
            meta_description: item?.meta_description || "",
            scheduleTimeDate: initialPublishDate,
          },
          isModified: false,
        };
      });
      bulkPortfoliosDataRef.current = initialCache;

      if (loadedRecords.length > 0) {
        applyPortfolioToForm(loadedRecords[0]);
      }
    } catch (error) {
      setIsNotification(true);
      setNotificationMsg(error?.message || "Failed to load portfolios for bulk edit");
      setSeverity("error");
    } finally {
      setFormSpinner(false);
    }
  };

  useEffect(() => {
    fetchPortfoliosByIds();
  }, [bulkPortfolioIds]);

  const activePortfolio = bulkPortfoliosList[activeBulkIndex] || null;

  const isPortfolioModified = (item, index) => {
    if (!item) return false;
    if (index === activeBulkIndex) {
      const currentPayload = buildPayload(currentFormValues);
      const baseline = originalPayloadRef.current || (originalDataRef.current ? buildPayload(originalDataRef.current) : null);
      const changed = baseline ? getChangedFields(currentPayload, baseline) : {};
      return Object.keys(changed).length > 0;
    }
    return Boolean(bulkPortfoliosDataRef.current[item._id]?.isModified);
  };

  const modifiedCount = useMemo(() => {
    return bulkPortfoliosList.reduce((acc, item, idx) => {
      return acc + (isPortfolioModified(item, idx) ? 1 : 0);
    }, 0);
  }, [bulkPortfoliosList, activeBulkIndex, currentFormValues]);

  const switchActivePortfolio = (newIndex) => {
    if (newIndex < 0 || newIndex >= bulkPortfoliosList.length) return;
    if (newIndex === activeBulkIndex) return;

    const currentPortfolio = bulkPortfoliosList[activeBulkIndex];
    if (currentPortfolio) {
      const currentPayload = buildPayload(currentFormValues);
      const baseline = originalPayloadRef.current || (originalDataRef.current ? buildPayload(originalDataRef.current) : null);
      const changed = baseline ? getChangedFields(currentPayload, baseline) : {};
      const isModified = Object.keys(changed).length > 0;

      bulkPortfoliosDataRef.current[currentPortfolio._id] = {
        values: { ...currentFormValues },
        original: originalDataRef.current,
        originalPayload: originalPayloadRef.current,
        isModified,
      };
    }

    setActiveBulkIndex(newIndex);
    const nextPortfolio = bulkPortfoliosList[newIndex];
    if (!nextPortfolio) return;

    const saved = bulkPortfoliosDataRef.current[nextPortfolio._id];
    if (saved) {
      originalDataRef.current = saved.original;
      originalPayloadRef.current = saved.originalPayload;

      setTitle(saved.values.title);
      setSlugName(saved.values.slugName);
      setSlugEdited(Boolean(saved.values.slugName));
      setSelectCategory(saved.values.selectCategory);
      setDescription(saved.values.description);
      setImage(saved.values.image);
      setMeta({
        meta_title: saved.values.meta_title,
        meta_description: saved.values.meta_description,
      });
      setIsMetaTitleCustom(Boolean(saved ? saved.values.meta_title : nextPortfolio?.meta_title));
      setScheduleTimeDate(saved.values.scheduleTimeDate || null);
      setWarn(false);
      setEnableSlug(false);
    } else {
      applyPortfolioToForm(nextPortfolio);
    }
  };

  const resetAndClose = useCallback(() => {
    bulkPortfoliosDataRef.current = {};
    if (onClose) onClose();
  }, [onClose]);

  const {
    showUnsavedModal,
    handleRequestClose: handleBulkEditCancel,
    handleConfirmLeave,
    handleStay,
  } = useUnsavedChanges(modifiedCount > 0, resetAndClose);

  const activePortfolioTitle = useMemo(() => {
    const active = bulkPortfoliosList[activeBulkIndex];
    return title || active?.title || "Portfolio";
  }, [bulkPortfoliosList, activeBulkIndex, title]);

  const filteredPortfoliosList = useMemo(() => {
    if (!portfolioSearchTerm.trim()) return bulkPortfoliosList;
    return bulkPortfoliosList.filter((b) =>
      (b.title || "").toLowerCase().includes(portfolioSearchTerm.toLowerCase())
    );
  }, [bulkPortfoliosList, portfolioSearchTerm]);

  const handleSetImage = (imgUrl) => {
    setImage(imgUrl || "");
  };

  const generateMetaData = async (sourceText, type) => {
    const sourceContent = sourceText?.replace(/<[^>]*>/g, " ").trim() || title || "";
    if (!sourceContent) {
      setIsNotification(true);
      setNotificationMsg(t("lang_Please_Enter_Title") || "Please enter title or description first");
      setSeverity("warning");
      return;
    }

    setAiloader(true);
    try {
      const payload = { name: sourceContent };
      if (type === "description") {
        const res = await API({
          url: "/admin/ai/category_meta_description",
          method: "post",
          data: payload,
        });
        setMeta((prev) => ({
          ...prev,
          meta_description: res?.data?.data || "",
        }));
      } else {
        const res = await API({
          url: "/admin/ai/category_meta_title",
          method: "post",
          data: payload,
        });
        setIsMetaTitleCustom(true);
        setMeta((prev) => ({
          ...prev,
          meta_title: res?.data?.data || "",
        }));
      }
    } catch (error) {
      setIsNotification(true);
      setNotificationMsg(error?.message || "AI metadata generation failed");
      setSeverity("error");
    } finally {
      setAiloader(false);
    }
  };

  const handleBulkUpdate = async () => {
    if (!title || !title.trim()) {
      setWarn(true);
      setIsNotification(true);
      setNotificationMsg("Portfolio Title is required!");
      setSeverity("error");
      return;
    }

    try {
      setBtnLoading(true);
      setFormSpinner(true);

      const currentPortfolio = bulkPortfoliosList[activeBulkIndex];
      if (currentPortfolio) {
        const currentPayload = buildPayload(currentFormValues);
        const baseline = originalPayloadRef.current || (originalDataRef.current ? buildPayload(originalDataRef.current) : null);
        const changed = baseline ? getChangedFields(currentPayload, baseline) : {};
        const isModified = Object.keys(changed).length > 0;

        bulkPortfoliosDataRef.current[currentPortfolio._id] = {
          values: { ...currentFormValues },
          original: originalDataRef.current,
          originalPayload: originalPayloadRef.current,
          isModified,
        };
      }

      const modifiedEntries = Object.entries(bulkPortfoliosDataRef.current).filter(
        ([_, data]) => data?.isModified
      );

      if (modifiedEntries.length === 0) {
        setIsNotification(true);
        setNotificationMsg("No changes detected to update");
        setSeverity("info");
        setBtnLoading(false);
        setFormSpinner(false);
        return;
      }

      for (const [id, data] of modifiedEntries) {
        if (!data.values.title || !data.values.title.trim()) {
          setIsNotification(true);
          setNotificationMsg("All modified Portfolios must have a Title");
          setSeverity("error");
          setBtnLoading(false);
          setFormSpinner(false);
          return;
        }
      }

      const bulkPayload = modifiedEntries
        .map(([id, data]) => {
          const payload = buildPayload(data.values);
          return {
            _id: id,
            ...payload,
          };
        })
        .filter(Boolean);

      const res = await API({
        url: "/admin/portfolio/BulkUpdatePortfolio",
        method: "post",
        data: bulkPayload,
      });

      if (res.status === 200 || res.status === 304) {
        setIsNotification(true);
        setNotificationMsg(
          `${bulkPayload.length} portfolio${bulkPayload.length > 1 ? "s" : ""} updated successfully!`
        );
        setSeverity("success");
        bulkPortfoliosDataRef.current = {};
        if (onSuccess) {
          onSuccess(bulkPayload.length);
        }
      } else {
        throw new Error(res?.data?.message || "Failed to update portfolios");
      }
    } catch (error) {
      console.error("Error bulk updating portfolios:", error);
      setIsNotification(true);
      setNotificationMsg(error?.response?.data?.message || error?.message || "Failed to update portfolios");
      setSeverity("error");
    } finally {
      setBtnLoading(false);
      setFormSpinner(false);
    }
  };

  const handleClose = () => {
    setIsNotification(false);
    setNotificationMsg("");
    setSeverity("");
  };

  return (
    <div className="flex flex-col h-screen w-screen overflow-hidden bg-[var(--primary-bg-color)] text-[var(--primary-text-color)] font-sans">
      {isNotification && notificationMsg && (
        <Notification message={notificationMsg} close={handleClose} severity={severity} />
      )}
      {(formSpinner || AiLoader) && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={formSpinner || AiLoader}
          className="flex flex-col"
        >
          <CircularProgress color="inherit" />
          <p className="mt-2 text-sm">
            {AiLoader ? t("lang_Please_Wait") || "Generating content..." : t("lang_loading") || "Processing..."}
          </p>
        </Backdrop>
      )}

      {/* Top Header Bar */}
      <div className="flex items-center justify-between px-6 py-3.5 border-b border-[var(--border-color)] bg-[var(--secondary-bg-color)] shrink-0">
        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={handleBulkEditCancel}
            className="w-7 h-7 rounded-full flex items-center justify-center text-[var(--table-text-color)] hover:bg-[var(--focus-tab-color)] hover:text-[var(--primary-text-color)] transition-colors cursor-pointer"
          >
            <X size={18} />
          </button>
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span className="!text-sm font-bold text-[var(--primary-text-color)] !m-0 leading-none">
                Bulk Edit Portfolios
              </span>
              <span className="bg-[var(--focus-tab-color)] text-[var(--primary-text-color)] text-[11px] font-semibold px-2 py-0.5 rounded-full border border-[var(--border-color)] leading-none">
                {bulkPortfoliosList.length} Selected
              </span>
            </div>
            <p className="!text-[11px] text-[var(--table-text-color)] !m-0 !mt-1">
              Select any portfolio from the list to view and edit its details.
            </p>
          </div>
        </div>
      </div>

      {/* Main Body */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Left Sidebar Panel */}
        <div className="w-[300px] border-r border-[var(--border-color)] bg-[var(--primary-bg-color)] p-4 flex flex-col shrink-0">
          {/* Outer Rounded Container */}
          <div className="flex-1 flex flex-col border border-[var(--border-color)] rounded-2xl bg-[var(--secondary-bg-color)] overflow-hidden shadow-[var(--shadow-card)]">
            {/* Header section */}
            <div className="p-3.5 border-b border-[var(--border-color)] flex items-center justify-between">
              <span className="text-xs font-bold text-[var(--primary-text-color)]">
                Selected Portfolios
              </span>
              <div className="flex items-center space-x-1 text-xs text-[var(--table-text-color)]">
                <button
                  type="button"
                  disabled={activeBulkIndex === 0}
                  onClick={() => switchActivePortfolio(activeBulkIndex - 1)}
                  className="p-1 rounded hover:bg-[var(--focus-tab-color)] disabled:opacity-30 disabled:pointer-events-none cursor-pointer text-[var(--table-text-color)] hover:text-[var(--primary-text-color)]"
                >
                  <ChevronLeft size={14} />
                </button>
                <span className="font-mono text-[11px] text-[var(--table-text-color)]">
                  {activeBulkIndex + 1}/{bulkPortfoliosList.length}
                </span>
                <button
                  type="button"
                  disabled={activeBulkIndex === bulkPortfoliosList.length - 1}
                  onClick={() => switchActivePortfolio(activeBulkIndex + 1)}
                  className="p-1 rounded hover:bg-[var(--focus-tab-color)] disabled:opacity-30 disabled:pointer-events-none cursor-pointer text-[var(--table-text-color)] hover:text-[var(--primary-text-color)]"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>

            {/* Filter Search Input Section */}
            <div className="p-3 border-b border-[var(--border-color)] bg-transparent">
              <input
                type="text"
                placeholder="Filter selected portfolios..."
                value={portfolioSearchTerm}
                onChange={(e) => setPortfolioSearchTerm(e.target.value)}
                className="w-full px-3 py-1.5 text-xs border border-[var(--border-color)] rounded-lg bg-[var(--primary-bg-color)] text-[var(--primary-text-color)] placeholder-[var(--table-text-color)] focus:outline-none focus:border-[var(--focus-border-color)] shadow-xs"
              />
            </div>

            {/* Items List */}
            <div className="flex-1 overflow-y-auto custom-scrollbar">
              {filteredPortfoliosList.map((portfolio) => {
                const actualIndex = bulkPortfoliosList.findIndex((item) => item._id === portfolio._id);
                const isActive = actualIndex === activeBulkIndex;
                const isModified = isPortfolioModified(portfolio, actualIndex);
                const displayTitle =
                  isActive ? (title || portfolio.title || "-") : (portfolio.title || "-");
                const currentImg =
                  isActive ? (image || portfolio.image) : (portfolio.image);
                const catName =
                  portfolio.portfoliocategory?.portfoliocategory_name ||
                  portfolio.portfoliocategory?.parent_category ||
                  "-";

                return (
                  <div
                    key={portfolio._id}
                    onClick={() => switchActivePortfolio(actualIndex)}
                    className={`p-3 cursor-pointer transition-colors flex items-center justify-between gap-3 border-b border-[var(--border-color)] last:border-b-0 ${isActive
                      ? "bg-[var(--focus-tab-color)]"
                      : "bg-[var(--secondary-bg-color)] hover:bg-[var(--focus-tab-color)]"
                      }`}
                  >
                    <div className="flex items-center space-x-3 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-md bg-[var(--primary-bg-color)] border border-[var(--border-color)] flex items-center justify-center shrink-0 overflow-hidden p-0.5 text-[var(--table-text-color)]">
                        <img
                          src={currentImg || NOPHOTO_IMAGE}
                          alt={displayTitle}
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = NOPHOTO_IMAGE;
                          }}
                        />
                      </div>
                      <div className="flex flex-col min-w-0 flex-1">
                        <span
                          className={`text-xs truncate ${isActive
                            ? "font-bold text-[var(--primary-text-color)]"
                            : "font-medium text-[var(--primary-text-color)]"
                            }`}
                        >
                          {displayTitle}
                        </span>
                        <span className="text-[11px] text-[var(--table-text-color)] truncate mt-0.5 capitalize">
                          {catName}
                        </span>
                      </div>
                    </div>

                    {isModified && (
                      <span
                        title='Modified'
                        className={`w-2 h-2 rounded-full bg-amber-500 shrink-0 select-none inline-block`}
                      />
                    )}
                  </div>
                );
              })}

              {filteredPortfoliosList.length === 0 && (
                <div className="p-4 text-center text-xs text-[var(--table-text-color)]">
                  No portfolios matching &quot;{portfolioSearchTerm}&quot;
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Main Form Content Area */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden bg-[var(--primary-bg-color)]">
          {/* Right Sub-Header Bar */}
          <div className="flex items-center justify-between px-6 py-2.5 bg-[var(--secondary-bg-color)] border-b border-[var(--border-color)] text-xs shrink-0">
            <div>
              <span className="font-bold text-[var(--primary-text-color)]">
                Currently Editing: {activePortfolioTitle}
              </span>{" "}
              <span className="text-[var(--table-text-color)] font-mono text-[11px]">
                ({activeBulkIndex + 1} of {bulkPortfoliosList.length})
              </span>
            </div>
            <div>
              {modifiedCount > 0 ? (
                <span className="text-emerald-500 font-medium text-xs">
                  {modifiedCount} portfolio modified
                </span>
              ) : (
                <span className="text-[var(--table-text-color)] text-xs font-normal">
                  No unsaved changes
                </span>
              )}
            </div>
          </div>

          {/* Form Area matching AddPortfolio.js exactly */}
          <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
            <form
              onKeyDown={(e) => e.key === "Enter" && e.target.tagName !== "TEXTAREA" && e.preventDefault()}
              className="max-w-4xl mx-auto flex flex-col gap-4 pb-12"
            >
              {/* Card 1: Title, URL Slug, Category & Description */}
              <DetailsComponent
                open={true}
                hasHr={false}
                showChevron={false}
                className="!pt-5 sm:!pt-[25px]"
              >
                <div className="w-full mb-4">
                  <label htmlFor="title" className="flex items-center justify-between">
                    <p className="text-[13px] font-medium text-[var(--primary-text-color)] m-0">
                      {t("lang_Portfolio_Title") || "Portfolio Title"}
                      <span className="text-red-500 ms-1">*</span>
                    </p>
                  </label>
                  <InputComponent
                    className={warn ? `border-red-500` : ``}
                    type="text"
                    name="title"
                    value={title}
                    onChange={(e) => {
                      const val = e.target.value;
                      setTitle(val);
                      setWarn(false);
                      if (!enableSlug && (!slugEdited || !slugName)) handleSlug(val);
                      if (!isMetaTitleCustom) {
                        setMeta((prev) => ({ ...prev, meta_title: val }));
                      }
                    }}
                    placeholder="e.g. Portfolio about your latest projects"
                    onBlur={() => {
                      if (!enableSlug && (!slugEdited || !slugName) && title) handleSlug(title);
                      if (!isMetaTitleCustom && !meta_title && title) {
                        setMeta((prev) => ({ ...prev, meta_title: title }));
                      }
                    }}
                  />
                  <div className="flex items-center justify-between mt-1 text-xs text-gray-500 w-full gap-1 min-h-[24px]">
                    <div className="flex items-center w-[90%] min-w-0">
                      <a
                        href={`${protocol}${clientInfo?.custom_domain || clientInfo?.customer_domain}${portfolioRoute}/${(slugName || '').toLowerCase()}`}
                        target="_blank"
                        rel="noreferrer"
                        className="font-medium shrink-0 mr-2 hover:underline cursor-pointer text-blue-600 flex items-center"
                      >
                        Preview
                      </a>
                      {/* INLINE EDIT */}
                      {!enableSlug ? (
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="truncate text-gray-500 min-w-0">
                            {`${protocol}${clientInfo?.custom_domain || clientInfo?.customer_domain}${portfolioRoute}/${(slugName || '').toLowerCase()}`}
                          </span>

                          <button
                            type="button"
                            onClick={() => {
                              setEnableSlug(true);
                              setTimeout(() => slugInputRef.current?.focus(), 50);
                            }}
                            className="text-gray-600 hover:text-black shrink-0 flex items-center cursor-pointer"
                          >
                            <Edit size={14} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 min-w-0 flex-1">
                          <span className="text-gray-400 shrink truncate max-w-[120px] sm:max-w-[200px] md:max-w-[300px] lg:max-w-[400px]">
                            {`${protocol}${clientInfo?.custom_domain || clientInfo?.customer_domain}${portfolioRoute}/`}
                          </span>

                          <InputComponent
                            ref={slugInputRef}
                            name="portfolio_slug_name"
                            className="flex-1 min-w-[60px] !h-[22px] !py-0.5 !px-1 text-xs"
                            value={slugName || ''}
                            onChange={(e) => {
                              setSlugName(e.target.value);
                              setSlugEdited(Boolean(e.target.value));
                            }}
                            onBlur={async () => {
                              if (slugName) {
                                const generated = await GenerateSlugName(slugName);
                                setSlugName(generated);
                              }
                            }}
                            onKeyDown={async (e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                if (!slugName) {
                                  setIsNotification(true);
                                  setSeverity("warning");
                                  setNotificationMsg("Slug can not be empty!");
                                  return;
                                }
                                const generated = await GenerateSlugName(slugName);
                                setSlugName(generated);
                                setEnableSlug(false);
                                setSlugEdited(true);
                              }
                            }}
                            isSlug={true}
                          />

                          <button
                            type="button"
                            className="text-green-600 ml-1 shrink-0 flex items-center cursor-pointer"
                            onClick={async () => {
                              if (!slugName) {
                                setIsNotification(true);
                                setSeverity("warning");
                                setNotificationMsg("Slug can not be empty!");
                                return;
                              }

                              const generated = await GenerateSlugName(slugName);
                              setSlugName(generated);
                              setEnableSlug(false);
                              setSlugEdited(true);
                            }}
                          >
                            <Check size={14} />
                          </button>
                        </div>
                      )}
                    </div>

                    <span className={`shrink-0 text-end w-[10%] flex items-center justify-end ${(title?.length || 0) > 60 ? 'text-red-500 font-semibold' : ''}`}>
                      {title?.length || 0}/60
                    </span>
                  </div>
                  {warn && (
                    <p className="text-red-500 text-xs mt-1">{t("lang_Please_Enter_Portfolio_Title") || "Please enter portfolio title"}</p>
                  )}
                </div>

                {/* Portfolio Category */}
                <div className="mb-4">
                  <label htmlFor="description" className="block text-[13px] font-medium text-[var(--primary-text-color)] mb-1">
                    {t("lang_Portfolio_Category") || "Portfolio Category"}
                  </label>
                  <SelectComponent
                    options={Category?.length
                      ? Category.map((category) => ({ value: category._id || category.id, label: category.parent_category || category.portfoliocategory_name }))
                      : []
                    }
                    value={selectCategory}
                    onChange={(e) => setSelectCategory(e.target.value)}
                    placeholder="Select portfolio category"
                    isClearable={false}
                  />
                </div>

                {/* Portfolio Description */}
                <div>
                  <label htmlFor="Description" className="block text-[13px] font-medium text-[var(--primary-text-color)] mb-1">
                    {t("lang_Portfolio_Description") || "Portfolio Description"}
                  </label>
                  <RichTextEditor
                    htmlData={description}
                    setHtmlData={(val) => setDescription(val)}
                    uplaodImageFile={uploadTextEditorImage}
                  />
                  <div className="flex w-full justify-end mt-1">
                    <p className={`text-xs mt-0 ${(description?.split(/\s+/)?.filter(Boolean)?.length || 0) > 280 ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
                      {`${description?.split(/\s+/)?.filter(Boolean)?.length || 0}/280 words`}
                    </p>
                  </div>
                </div>

                {/* Publish Date */}
                <div className="mt-4">
                  <label className="block text-[13px] font-medium text-[var(--primary-text-color)] mb-1">
                    {t("lang_Publish_Date") || "Publish Date"}
                  </label>
                  {scheduleTimeDate ? (
                    <div className="flex items-center gap-3">
                      <ButtonComponent
                        variant="outline"
                        type="button"
                        onClick={() => setIsSchedule(true)}
                      >
                        {t("lang_Update_Publish_Date") || "Update publish date"}
                      </ButtonComponent>
                      <div className="flex items-center justify-between px-3 py-2 bg-[#f8fafc] dark:bg-zinc-800/60 border border-gray-200 dark:border-zinc-700 rounded-lg flex-1">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {new Date(scheduleTimeDate).toLocaleString("en-GB", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                            hour12: false,
                          })}
                        </span>
                        <button
                          type="button"
                          className="text-red-500 hover:text-red-600 text-sm font-normal bg-transparent border-none p-0 cursor-pointer ml-3"
                          onClick={() => setScheduleTimeDate(null)}
                        >
                          {t("lang_Clear") || "Clear"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <ButtonComponent
                        variant="outline"
                        type="button"
                        onClick={() => setIsSchedule(true)}
                      >
                        {t("lang_Set_Publish_Date") || "Set publish date"}
                      </ButtonComponent>
                    </div>
                  )}
                </div>
              </DetailsComponent>

              {/* Card 2: Media */}
              <DetailsComponent
                title={
                  <span>
                    {t("lang_media") || "Media"}
                    <span className="text-red-500 ms-1">*</span>
                  </span>
                }
                subtitle={t("lang_Upload_Captivating_Image_To_Make_Your_Category_Stand_Out") || "Upload captivating image to make your portfolio stand out"}
                open={true}
              >
                <div>
                  <label htmlFor="Image" className="flex items-center justify-between mb-1">
                    <p className="text-[13px] font-medium text-[var(--primary-text-color)] m-0">{t("lang_Image") || "Image"}</p>
                  </label>
                  <ImageUploader
                    alreadyImage={image}
                    handleSetImage={handleSetImage}
                    folder="portfolio"
                    format="image"
                  />
                </div>
              </DetailsComponent>

              {/* Card 3: Search Engine Listing */}
              <DetailsComponent
                title={t("lang_search_engine_listing") || "Search engine listing"}
                subtitle={t("lang_search_engine_listing_detail") || "Add a title and description to see how this portfolio post might appear in a search engine listing"}
                open={true}
                contentClassName="px-4 sm:px-[25px] pb-4 sm:pb-[25px]"
              >
                <div className="mb-4">
                  <AiTextGenerate
                    fieldLabel={t("lang_meta_title") || "Meta Title"}
                    label={t("lang_get_meta_title") || "Generate"}
                    tooltip={t("lang_meta_tooltip", { type: "title" }) || "Generate SEO Title"}
                    onGenerate={() => generateMetaData(description || title, "title")}
                  />
                  <InputComponent
                    type="text"
                    id="Meta Tag Title"
                    name="meta_title"
                    value={meta_title}
                    onChange={(e) => {
                      setIsMetaTitleCustom(Boolean(e.target.value));
                      setMeta((prev) => ({ ...prev, [e.target.name]: e.target.value }));
                    }}
                    placeholder="Enter meta title"
                  />
                  <div className="flex w-full justify-end mt-1">
                    <p className={`text-xs mt-0 ${(meta_title?.length || 0) > 60 ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
                      {`${meta_title?.length || 0}/60`}
                    </p>
                  </div>
                </div>

                <div>
                  <AiTextGenerate
                    fieldLabel={t("lang_meta_description") || "Meta Description"}
                    label={t("lang_get_meta_description") || "Generate"}
                    tooltip={t("lang_meta_tooltip", { type: "description" }) || "Generate SEO Description"}
                    onGenerate={() => generateMetaData(description || title, "description")}
                  />
                  <textarea
                    className="w-full h-24 block px-3 py-2 border border-[var(--border-color)] rounded-lg focus:outline-none focus:border-[var(--focus-border-color)] bg-[var(--secondary-bg-color)] text-[var(--primary-text-color)] text-sm"
                    id="Meta Tag description"
                    name="meta_description"
                    value={(meta_description || '').replace(/<[^>]+>/g, '')}
                    onChange={(e) => {
                      setMeta({ ...meta, [e.target.name]: e.target.value });
                    }}
                    placeholder="Enter meta description"
                  />
                  <div className="flex w-full justify-end mt-1">
                    <p className={`text-xs mt-0 ${((meta_description || '').replace(/<[^>]+>/g, '').length || 0) > 160 ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
                      {`${(meta_description || '').replace(/<[^>]+>/g, '').length || 0}/160 words`}
                    </p>
                  </div>
                </div>
              </DetailsComponent>
            </form>
          </div>
        </div>
      </div>

      {/* Sticky Bottom Action Bar */}
      <div className="flex items-center justify-between w-full border-t border-[var(--border-color)] bg-[var(--secondary-bg-color)] p-3.5 px-6 shrink-0">
        <button
          type="button"
          onClick={handleBulkEditCancel}
          className="flex items-center text-xs font-medium text-[var(--table-text-color)] hover:text-[var(--primary-text-color)] transition-colors gap-1 cursor-pointer"
        >
          ← cancel
        </button>

        <div className="flex items-center gap-2">
          <ButtonComponent
            variant="primary"
            size="sm"
            loading={btnLoading}
            disabled={modifiedCount === 0 || btnLoading}
            onClick={handleBulkUpdate}
          >
            {modifiedCount > 0 ? `Save Changes (${modifiedCount})` : "Save Changes"}
          </ButtonComponent>
        </div>
      </div>

      <Dialog
        open={isSchedule}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => setIsSchedule(false)}
        aria-describedby="alert-dialog-slide-description"
        sx={{
          zIndex: 1000000,
          "& .MuiDialog-paper": {
            zIndex: 1000000,
          },
        }}
      >
        <div className="w-full sm:w-[450px] p-6 rounded-[12px] bg-white dark:bg-zinc-900 text-gray-900 dark:text-zinc-100 shadow-xl flex flex-col">
          <div className="flex items-center justify-between pb-3 mb-4 border-b border-gray-200 dark:border-zinc-800">
            <h5 className="text-base font-semibold text-gray-900 dark:text-white m-0">
              {t("lang_Set_Publish_Date") || "Set publish date"}
            </h5>
            <button
              type="button"
              onClick={() => setIsSchedule(false)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1 rounded-md transition-colors border-none bg-transparent cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div className="relative">
              <input
                type="text"
                readOnly
                value={
                  scheduleTimeDate
                    ? new Date(scheduleTimeDate).toLocaleDateString("en-US", {
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      }) +
                      " " +
                      new Date(scheduleTimeDate).toLocaleTimeString("en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })
                    : ""
                }
                placeholder={t("lang_Select_Date_Time") || "Select date & time"}
                className="w-full px-3 py-2 border border-gray-200 dark:border-zinc-700 rounded-lg text-sm bg-gray-50 dark:bg-zinc-800 text-gray-900 dark:text-white pr-10 focus:outline-none"
              />
              <CalendarFill className="absolute right-3 top-3 w-4 h-4 text-gray-400" />
            </div>

            <div className="w-full min-h-[290px] custom-datepicker-inline flex justify-center">
              <div className="relative w-full flex justify-center">
                <DatePicker
                  className={`${scheduleTimeDate ? "bold-date" : ""} w-full px-3 py-2 border border-gray-200 dark:border-zinc-700 rounded-[6px] focus:outline-none focus:border-gray-400 bg-white dark:bg-zinc-800 text-gray-900 dark:text-white`}
                  showTimeSelect
                  inline
                  selected={scheduleTimeDate ? new Date(scheduleTimeDate) : null}
                  onChange={(date) => setScheduleTimeDate(date)}
                  dateFormat="MMMM d, yyyy h:mm aa"
                />
              </div>
            </div>

            <div className="pt-2">
              <ButtonComponent
                variant="primary"
                type="button"
                onClick={() => setIsSchedule(false)}
              >
                {t("lang_Done") || "Done"}
              </ButtonComponent>
            </div>
          </div>
        </div>
      </Dialog>

      <UnsavedChangesModal
        open={showUnsavedModal}
        onStay={handleStay}
        onLeave={handleConfirmLeave}
      />
    </div>
  );
};

export default BulkEditPortfolio;
